/* ============================================
   COMMENTARY ENGINE
   ============================================
   Main controller for AI commentary.
   Manages: context → API call → audio playback
   with ducking of stadium audio.

   Works in two modes:
   - Iframe mode: emits postMessage to parent
   - Standalone mode: plays audio directly (playground)
   ============================================ */

import { buildCommentaryContext, buildIdleContext } from './commentary-context.js'
import { CommentaryQueue } from './commentary-queue.js'
import { PossessionPhaseTracker } from './possession-phase-tracker.js'
import { getEventPriority } from '../events/event-mapping.js'
import { state } from '../state.js'

const API_BASE = '/api/commentary'

export class CommentaryEngine {
  constructor(options = {}) {
    this.enabled = false
    this.audioContext = options.audioContext || null
    this.commentaryGain = null

    // Audio element for MediaSource streaming
    this.audioEl = null
    this.mediaSource = null
    this.sourceBuffer = null
    this.sourceNode = null
    this.pendingChunks = []
    this.mediaSourceReady = false

    // Config
    this.config = {
      volume: options.volume ?? 1.0,
      idleTimerMs: options.idleTimerMs ?? 45000,
      systemPrompt: options.systemPrompt || null,
      lang: options.lang || 'en'
    }

    // Possession phase tracker
    this.phaseTracker = new PossessionPhaseTracker()

    // State
    this.currentController = null // AbortController for current request

    // Log callback
    this.onLog = options.onLog || ((msg) => console.log(msg))

    // Priority queue
    this.queue = new CommentaryQueue({
      idleTimerMs: this.config.idleTimerMs,
      onGenerate: (item) => this._generate(item),
      onCancel: () => this._cancel(),
      onIdle: () => this._onIdle(),
      onLog: (msg) => this.onLog(msg)
    })
  }

  /* ──────────────────────────────────────────
     Init & Audio Setup
     ────────────────────────────────────────── */

  /**
   * Initialize the audio graph for commentary.
   * Call this after the AudioContext is created (user gesture).
   */
  init(audioContext) {
    this.audioContext = audioContext || this.audioContext

    if (!this.audioContext) {
      this.onLog('[COMMENTARY] No AudioContext — will work in text-only mode')
      return
    }

    // Create commentary gain node
    this.commentaryGain = this.audioContext.createGain()
    this.commentaryGain.gain.value = this.config.volume

    // Commentary gain connects to the AudioContext destination
    // (in the Studio, it would connect to the master bus)
    this.commentaryGain.connect(this.audioContext.destination)

    // Create reusable audio element
    this._createAudioElement()

    this.onLog('[COMMENTARY] Audio graph initialized')
  }

  _createAudioElement() {
    this.audioEl = document.createElement('audio')
    this.audioEl.preload = 'auto'

    // Connect to Web Audio graph for ducking
    if (this.audioContext && this.commentaryGain) {
      this.sourceNode = this.audioContext.createMediaElementSource(this.audioEl)
      this.sourceNode.connect(this.commentaryGain)
    }

    // When commentary ends, restore stadium volume
    this.audioEl.addEventListener('ended', () => {
      if (this._safetyTimer) { clearTimeout(this._safetyTimer); this._safetyTimer = null }
      if (this._timeupdateHandler) {
        this.audioEl.removeEventListener('timeupdate', this._timeupdateHandler)
        this._timeupdateHandler = null
      }
      this.queue.onCommentaryEnd()
      this.onLog('[COMMENTARY] Playback ended')
    })

    this.audioEl.addEventListener('error', (e) => {
      if (this._safetyTimer) { clearTimeout(this._safetyTimer); this._safetyTimer = null }
      this.onLog(`[COMMENTARY] Audio error: ${e.message || 'unknown'}`)
      this.queue.onCommentaryEnd()
    })
  }

  /* ──────────────────────────────────────────
     Public API
     ────────────────────────────────────────── */

  enable() {
    this.enabled = true
    this.onLog('[COMMENTARY] Enabled')
  }

  disable() {
    this.enabled = false
    this._cancel()
    this.queue.destroy()
    this.phaseTracker.reset()
    this.onLog('[COMMENTARY] Disabled')
  }

  setVolume(v) {
    this.config.volume = v
    if (this.commentaryGain) {
      this.commentaryGain.gain.value = v
    }
  }

  setConfig(cfg) {
    Object.assign(this.config, cfg)
    if (cfg.volume !== undefined) this.setVolume(cfg.volume)
  }

  /**
   * Called when an event is detected (from ball-controller or postMessage).
   *
   * @param {string} eventType
   * @param {string} team - 'HOME', 'AWAY', 'neutral'
   */
  onEvent(eventType, team = 'neutral') {
    if (!this.enabled) return

    // Feed event to phase tracker with current ball position
    const x = state.currentBallPosition?.x ?? 0.5
    const y = state.currentBallPosition?.y ?? 0.5
    this.phaseTracker.onEvent(eventType, team, x, y)

    const { priority } = getEventPriority(eventType)
    const context = buildCommentaryContext(eventType, team, this.phaseTracker)

    this.onLog(`[COMMENTARY] Event: "${eventType}" (P${priority}, ${team})`)
    this.queue.push(eventType, priority, team, context)
  }

  /**
   * Get status for debugging.
   */
  getStatus() {
    return {
      enabled: this.enabled,
      hasAudio: !!this.audioContext,
      volume: this.config.volume,
      queue: this.queue.getStatus()
    }
  }

  /* ──────────────────────────────────────────
     Internal: Generate & Play
     ────────────────────────────────────────── */

  async _generate(item) {
    const startTime = Date.now()
    this.onLog(`[COMMENTARY] Generating for "${item.eventType}"...`)

    // Abort any previous request
    this._cancel()
    this.currentController = new AbortController()

    try {
      const body = { ...item.context }
      if (this.config.systemPrompt) {
        body.systemPrompt = this.config.systemPrompt
      }
      if (this.config.lang) {
        body.lang = this.config.lang
      }

      const response = await fetch(`${API_BASE}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: this.currentController.signal
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      // Process SSE stream
      await this._processSSEStream(response, startTime)

    } catch (err) {
      if (err.name === 'AbortError') {
        this.onLog('[COMMENTARY] Request cancelled')
      } else {
        this.onLog(`[COMMENTARY] Error: ${err.message}`)
      }
      this.queue.onCommentaryEnd()
    }
  }

  async _processSSEStream(response, startTime) {
    const reader = response.body.getReader()
    const decoder = new TextDecoder()

    let textAccumulator = ''
    let audioChunks = []
    let firstAudioReceived = false

    // Prepare MediaSource for streaming playback
    this._prepareMediaSource()

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      const text = decoder.decode(value, { stream: true })
      const lines = text.split('\n')

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue

        try {
          const data = JSON.parse(line.slice(6))

          switch (data.type) {
            case 'start':
              this.onLog(`[COMMENTARY] Pipeline started`)
              break

            case 'text':
              textAccumulator += data.chunk
              this.onLog(`[COMMENTARY] LLM: "${data.chunk.trim()}"`)
              break

            case 'audio':
              if (!firstAudioReceived) {
                firstAudioReceived = true
                this.onLog(`[COMMENTARY] First audio chunk received`)
              }
              this._appendAudioChunk(data.chunk)
              if (data.isFinal) {
                this._finalizeAudio()
              }
              break

            case 'latency':
              this.onLog(`[COMMENTARY] First audio latency: ${data.firstAudioMs}ms`)
              break

            case 'complete': {
              const totalMs = Date.now() - startTime
              const mode = data.llmMock ? 'MOCK' : 'LIVE'
              this.onLog(`[COMMENTARY] Complete [${mode}]: "${data.text}" (${totalMs}ms, ${data.audioChunks} audio chunks)`)
              if (data.llmError) {
                this.onLog(`[ERROR] LLM fallback reason: ${data.llmError}`)
              }
              if (data.ttsMock) {
                this.onLog(`[AUDIO] TTS running in mock mode (no ElevenLabs)`)
              }
              break
            }

            case 'timeout':
              this.onLog(`[COMMENTARY] Pipeline timeout after ${data.elapsed}ms`)
              break

            case 'error':
              this.onLog(`[COMMENTARY] Pipeline error: ${data.message}`)
              break
          }
        } catch (e) {
          // Ignore SSE parse errors
        }
      }
    }

    if (!firstAudioReceived) {
      // No audio received (text-only / mock without audio) — end now
      this.onLog(`[COMMENTARY] No audio received (text only): "${textAccumulator}"`)
      this.queue.onCommentaryEnd()
    } else {
      // Audio was received — ensure MediaSource is finalized
      // (isFinal may not always arrive from the TTS)
      this._finalizeAudio()

      // Safety timeout: if audio 'ended' never fires, force release after 8s
      this._safetyTimer = setTimeout(() => {
        if (this.queue.playing) {
          this.onLog('[COMMENTARY] Safety timeout — forcing end')
          this.queue.onCommentaryEnd()
        }
      }, 8000)
    }
  }

  /* ──────────────────────────────────────────
     MediaSource Streaming
     ────────────────────────────────────────── */

  _prepareMediaSource() {
    this.pendingChunks = []
    this.mediaSourceReady = false

    // Revoke previous MediaSource URL to free memory
    if (this.audioEl.src && this.audioEl.src.startsWith('blob:')) {
      URL.revokeObjectURL(this.audioEl.src)
    }

    // If we can use MediaSource (browser support check)
    if (typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg')) {
      this.mediaSource = new MediaSource()
      this.audioEl.src = URL.createObjectURL(this.mediaSource)

      this.mediaSource.addEventListener('sourceopen', () => {
        try {
          this.sourceBuffer = this.mediaSource.addSourceBuffer('audio/mpeg')
          this.mediaSourceReady = true

          // Append any chunks that arrived before sourceopen
          this._flushPendingChunks()

          this.sourceBuffer.addEventListener('updateend', () => {
            this._flushPendingChunks()
          })
        } catch (e) {
          this.onLog(`[COMMENTARY] MediaSource error: ${e.message}`)
        }
      })
    }
  }

  _appendAudioChunk(base64Chunk) {
    const binary = atob(base64Chunk)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }

    if (this.mediaSourceReady && this.sourceBuffer && !this.sourceBuffer.updating) {
      try {
        this.sourceBuffer.appendBuffer(bytes)
        // Start playback when we have enough data
        if (this.audioEl.paused && this.audioEl.readyState >= 2) {
          this.audioEl.play().catch(() => {})
        }
      } catch (e) {
        this.pendingChunks.push(bytes)
      }
    } else {
      this.pendingChunks.push(bytes)
    }
  }

  _flushPendingChunks() {
    while (
      this.pendingChunks.length > 0 &&
      this.sourceBuffer &&
      !this.sourceBuffer.updating
    ) {
      try {
        this.sourceBuffer.appendBuffer(this.pendingChunks.shift())
      } catch (e) {
        break
      }
    }

    // Try to start playback
    if (this.audioEl.paused && this.audioEl.readyState >= 2) {
      this.audioEl.play().catch(() => {})
    }
  }

  _finalizeAudio() {
    // Wait for all pending chunks to be appended, then end stream
    const checkAndEnd = () => {
      if (this.pendingChunks.length === 0 && this.sourceBuffer && !this.sourceBuffer.updating) {
        try {
          if (this.mediaSource.readyState === 'open') {
            this.mediaSource.endOfStream()
          }
        } catch (e) {
          // Already ended
        }
        // Monitor timeupdate as fallback — 'ended' event is unreliable with MediaSource
        this._monitorPlaybackEnd()
      } else {
        setTimeout(checkAndEnd, 50)
      }
    }
    checkAndEnd()
  }

  _monitorPlaybackEnd() {
    if (!this.audioEl) return
    // Remove previous monitor if any
    if (this._timeupdateHandler) {
      this.audioEl.removeEventListener('timeupdate', this._timeupdateHandler)
    }
    this._timeupdateHandler = () => {
      const dur = this.audioEl.duration
      if (isFinite(dur) && dur > 0 && this.audioEl.currentTime >= dur - 0.05) {
        this.audioEl.removeEventListener('timeupdate', this._timeupdateHandler)
        this._timeupdateHandler = null
        if (this._safetyTimer) { clearTimeout(this._safetyTimer); this._safetyTimer = null }
        if (this.queue.playing) {
          this.queue.onCommentaryEnd()
          this.onLog('[COMMENTARY] Playback ended (timeupdate)')
        }
      }
    }
    this.audioEl.addEventListener('timeupdate', this._timeupdateHandler)
  }

  /* ──────────────────────────────────────────
     Cancel & Idle
     ────────────────────────────────────────── */

  _cancel() {
    if (this._safetyTimer) { clearTimeout(this._safetyTimer); this._safetyTimer = null }
    if (this._timeupdateHandler && this.audioEl) {
      this.audioEl.removeEventListener('timeupdate', this._timeupdateHandler)
      this._timeupdateHandler = null
    }
    if (this.currentController) {
      this.currentController.abort()
      this.currentController = null
    }
    // Stop audio playback
    if (this.audioEl) {
      this.audioEl.pause()
      this.audioEl.currentTime = 0
    }
  }

  _onIdle() {
    if (!this.enabled) return
    this.onLog('[COMMENTARY] Idle — generating context commentary')

    const context = buildIdleContext(this.phaseTracker)
    this.queue.push('idle', 0, 'neutral', context)
  }

  destroy() {
    this.disable()
    if (this.audioEl) {
      this.audioEl.pause()
      this.audioEl.src = ''
    }
    if (this.sourceNode) {
      try { this.sourceNode.disconnect() } catch (e) {}
    }
    if (this.commentaryGain) {
      try { this.commentaryGain.disconnect() } catch (e) {}
    }
  }
}

// Singleton for standalone (playground) usage
let _instance = null
export function getCommentaryEngine(options) {
  if (!_instance) {
    _instance = new CommentaryEngine(options)
  }
  return _instance
}
